import { useContext, useMemo, useState } from "react"
import BasePage from "../../../components/layout/BasePage"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ModalContext } from "../../../components/contexts/ModalContext"
import BaseFilterButton from "../../../components/elements/BaseFilterButton"
import CustomerCard from "../components/CustomerCard"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import ModalCheckAction from "../../../components/elements/ModalCheckAction"
import CustomerActionModal from "../components/CustomerActionModal"
import AddCustomerModal from "../components/AddCustomerModal"
import { useNavigate } from "react-router-dom"
import { checkDate, getDataFromSessionStorage, useDelayWhenSearchWithPagination } from "../../../services/functions"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import { useArchiveDataMutation, useDeleteDataMutation, useRestoreMutation, useStartExcelExportMutation } from "../../api/services"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { messageError, messageInfo } from "../../../services/messages"
import DeleteWithZipModal from "../../../components/elements/DeleteWithZipModal"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import { ReactComponent as EXPORT } from "../../../assets/DownloadICON.svg"
import { ReactComponent as CIRCLEPLUS } from "../../../assets/PlusInCircleIcon.svg"
import BasePopover from "../../../components/elements/BasePopover"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import BaseInputSearch from "../../../components/elements/BaseInputSearch"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"

export default function Kunden() {
    const navigate = useNavigate()

    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.CUSTOMER_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.CUSTOMER_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.CUSTOMER_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm as string, LocalStorageKeys.CUSTOMER_FILTER + "searchTerm")
    const [filterArchived, setfilterArchived] = useState<boolean | undefined>(
        getDataFromSessionStorage(LocalStorageKeys.CUSTOMER_FILTER + "archived")
    )
    const [filterDelete, setfilterDelete] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.CUSTOMER_FILTER + "deleted"))

    // Get Data Query
    const queryKey = GeneralQueryKeys.CUSTOMER_ALL + page + searchParams + filterArchived + filterDelete
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({ pageType: "customer", page: page, limit: 20, search: searchParams, archived: filterArchived, deleted: filterDelete }),
    })

    // NOTE -> Filter Variables
    const [showFilter, setShowFilter] = useState(false)
    const modalCon = useContext(ModalContext)

    // reset filter settings
    const handleRemoveFilter = () => {
        setfilterArchived(false)
        setfilterDelete(false)
    }

    const displayedCustomers = useMemo(() => data?.docs, [data?.docs])

    // Archive Customer
    const queryClient = useQueryClient()
    const { mutate: archiveCustomer } = useArchiveDataMutation(queryClient, () => modalCon?.trigger(3), queryKey, "Kundenstatus")
    const [selectedCustomerId, setselectedCustomerId] = useState("")
    const handleArchiveCustomer = (cid: string, archive: boolean) => {
        archiveCustomer({
            id: cid,
            pageType: "customer",
            body: { archived: archive },
        })
    }

    // Delete Customer
    const { mutate: deleteCustomer } = useDeleteDataMutation(queryClient, modalCon, "Kunde", queryKey)
    const handleDeleteCustomer = () => {
        if (
            displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId).lastSuccessfulZipExport &&
            checkDate(displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId).lastSuccessfulZipExport, 7)
        ) {
            deleteCustomer({ id: selectedCustomerId, pageType: "customer" })
        } else {
            messageError(
                "a1b2c3",
                "Fehler!",
                "Etwas ist schiefgelaufen. Bitte wenden Sie sich mit einer ausführlichen Problembeschreibung an den Administrator."
            )
        }
    }

    // Start Excel Export of CUSTOMER
    const { mutate: startExcelExport } = useStartExcelExportMutation()

    // Restore Customer
    const { mutate: restoreCustomer } = useRestoreMutation(queryClient, "Kunden", queryKey)
    const handleRestoreCustomer = (id: string) => {
        restoreCustomer({ id: id, pageType: "customer" })
    }

    return (
        <BasePage>
            <BaseHeadBanner
                title={"Kunden"}
                button={
                    <Authorized allOf={[P.kunden_create]}>
                        <div className="flex gap-4">
                            <BasePopover
                                placement="bottomLeft"
                                label={"Export starten"}
                                text="Bei Bestätigung wird eine Excel-Datei an Ihre E-Mail gesendet, welche eine Tabelle aller aktuellen Objekte enthält."
                                okButtonClick={() => startExcelExport({ collectionType: "customer" })}>
                                <EXPORT className="w-8 h-8 cursor-pointer" />
                            </BasePopover>

                            <CIRCLEPLUS className="w-8 h-8 cursor-pointer" onClick={() => modalCon?.trigger(1)} />
                        </div>
                    </Authorized>
                }
            />
            <BaseButtonRow>
                <BaseInputSearch setsearchTerm={setsearchTerm} searchTerm={searchTerm} placeholder="Kunden suchen..." />
                <BaseFilterButton func={() => setShowFilter(!showFilter)} />
                <BaseFilterModal
                    visible={showFilter}
                    setVisible={() => setShowFilter(false)}
                    searchArchived={filterArchived}
                    changeArchived={setfilterArchived}
                    removeFilter={handleRemoveFilter}
                    statusType={"customer"}
                    searchDeleted={filterDelete}
                    changeDeleted={setfilterDelete}
                    sessionStorageKey={LocalStorageKeys.CUSTOMER_FILTER}
                />
            </BaseButtonRow>
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !isLoading && !error && (
                <>
                    {/* Body with Cards */}
                    <BaseGrowBody classProps="overflow-y-scroll overflow-x-hidden flex flex-col md:inline-grid md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 pt-4 pb-8 w-full">
                        {displayedCustomers?.map((item: any) => (
                            <CustomerCard
                                bills={item.billCount}
                                offers={item.offerCount}
                                objects={item.objectCount}
                                deleted={item.softDeleteAfter}
                                nr={item.debtorIdentifier}
                                restoreFunc={() => handleRestoreCustomer(item._id)}
                                key={item._id}
                                name={item.customer?.name}
                                owner={item.administrator?.name}
                                adress={item.customer?.adress}
                                archived={item.archived}
                                openFunc={() => {
                                    if (item.softDeleteAfter) {
                                        messageInfo(
                                            "1a2b3c",
                                            "Das klappt gerade nicht!",
                                            'Dieser Kunden ist als "gelöscht" markiert und kann deswegen nicht mehr bearbeitet werden. Stellen Sie den Kunden erst wieder her, bevor zu ihn bearbeiten.',
                                            10
                                        )
                                    } else {
                                        navigate("/kunden/" + item._id)
                                    }
                                }}
                                archiveFunc={() => handleArchiveCustomer(item._id, !item.archived)}
                                deleteFunc={() => {
                                    if (item.lastSuccessfulZipExport && checkDate(item.lastSuccessfulZipExport, 7)) {
                                        setselectedCustomerId(item._id)
                                        modalCon?.trigger(4)
                                    } else {
                                        setselectedCustomerId(item._id)
                                        modalCon?.trigger(6)
                                    }
                                }}
                            />
                        ))}
                        {(!displayedCustomers || displayedCustomers.length === 0) && (
                            <div className="flex flex-col justify-center items-center w-full">
                                <p className="text-gray-300 uppercase font-medium pt-4">Keine Kunden angelegt</p>
                                <button className="btn btn-ghost mt-2 btn-outline max-w-xs" onClick={() => setsearchTerm("")}>
                                    Eingabe zurücksetzen
                                </button>
                            </div>
                        )}
                    </BaseGrowBody>
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            defaultCurrent={1}
                            pageSize={20}
                            current={page}
                            onChange={handlePageChange}
                            total={data.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                    <AddCustomerModal queryKey={queryKey} />
                    <CustomerActionModal
                        objectAction={() => {
                            modalCon?.trigger(2)
                            modalCon?.trigger(9)
                        }}
                    />
                    <ModalCheckAction
                        modalId={4}
                        headline={"Wirklich Löschen?"}
                        text={
                            "Sind Sie sich sicher, diesen Kunden zu löschen? Alle dazugehörigen Daten werden ebenfalls gelöscht. Der Button wird freigegeben, sobald der ZIP-Export heruntergeladen wurde."
                        }
                        buttonText={"Löschen"}
                        buttonColor="btn-error"
                        func={handleDeleteCustomer}
                        disabled={
                            !displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId)?.zipExportDownloadedBy ||
                            displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId)?.zipExportDownloadedBy?.length ===
                                0 ||
                            !displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId)?.lastSuccessfulZipExport ||
                            !checkDate(
                                displayedCustomers?.find((item: { _id: string }) => item._id === selectedCustomerId)?.lastSuccessfulZipExport,
                                7
                            )
                        }
                    />
                    <DeleteWithZipModal
                        modalId={6}
                        title={"Kunde"}
                        queryKey={queryKey}
                        data={{ id: selectedCustomerId, pageType: "customer" }}
                        closeModal={() => modalCon?.trigger(6)}
                    />
                </>
            )}
        </BasePage>
    )
}
